import { useContext, useEffect, useMemo, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";

import { TabPanels, Tabs } from "@reach/tabs";
import styled from "styled-components/macro";
import { Auth } from "../../../components/Auth";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { Tab } from "../../../components/ReachTabs/Tab";
import { TabList } from "../../../components/ReachTabs/TabList";
import { LazyTabPanel } from "../../../components/ReachTabs/TabPanel";
import type { DataMutate, Permission } from "../../../types/types";
import type {
  AttributeGroupSummary,
  CollectionSchema,
  PIMProduct,
  SupportedAssetAccessibility,
  TemplateTabData,
} from "../../../types/types.PIM";
import { AccessHomePage } from "./Access/AccessHomePage";
import { ActivityHistory } from "./Activity/ActivityHistory";
import { ProductAssets } from "./Assets/ProductAssetsTabs";
import type { IProductVisibilityData } from "./Connections/Connections";
import { ProductConnections } from "./Connections/Connections";
import { ProductGroupCard } from "./ProductOverview/ProductGroupCard";
import { SchemaTagCard } from "./ProductOverview/SchemaTagCard";
import { ProductPricingPortfolioTab } from "./ProductSkuListPrice/ProductPricingPortfolioTab";
import { ProductPricingTab } from "./ProductSkuListPrice/ProductPricingTab";
import { ProductTabPanel } from "./ProductTabPanel";
import {
  useIsActylisStorefront,
  useMediaQueries,
  useStoreState,
} from "../../../util/util";
import { Approvals } from "./Approvals";
import { ProductProvider } from "./ProductContext";
import {
  RelatedEntitiesTabContent,
  getShouldShowRelatedEntitiesTab,
} from "../../../components/ObjectReference/RelatedEntitiesTab";
import { useRoutePath } from "../../../util/Routing";
import { getProductTypePermissionSuffix } from "./ProductOverview/product_overview.utils";
import { ChangeRequestsListPage } from "../../../components/ChangeRequestForm/ChangeRequestTable";
import { RelatedEntitiesResponse } from "../../../types/types.MDM";

const ProductTab = styled(Tab).attrs({
  "data-event-type": "product_tab",
  "data-tab-type": "product_tab",
})``;

type TabDefinition = {
  key: string;
  label: string;
  analyticsName: string;
  renderPanel: (isTabActive: boolean) => ReactNode;
};

function withTabLoadingPanel(
  isTabActive: boolean,
  isLoading: boolean,
  content: ReactNode
): ReactNode {
  if (!isTabActive) {
    return null;
  }
  if (isLoading) {
    return <DelayedSpinner />;
  }
  return content;
}

export const ProductTabs = ({
  product,
  hasPermission,
  t,
  replaceProductId,
  mutate,
  index,
  onChange,
  tabsKey,
  tabsStyle,
  isPortfolio = false,
  roleIsGuest = false,
  roleIsBuyer = false,
  onVisibilityDataFetched,
  show_pricing_tab,
  isTabDisabled = false,
  isShowTableWithBulkAction = false,
  isShowGridAssetWithBulkAction = false,
  documentType,
  isApprovalWorkflowEnabled = false,
  isApprovalWorkflowLoading = false,
  isDocumentAvailabilityLoading = false,
  isListPricesLoading = false,
  isRelatedEntitiesLoading = false,
  onApprovalsCountChange,
  hasAssetsOrExternalLinks,
  relatedEntities,
  onDocumentTypeTabChange,
  requestedTabKey,
  onRequestedTabChange,
}: {
  product: PIMProduct;
  hasPermission: (p?: Permission | Permission[] | undefined) => boolean;
  t: (s: string) => string;
  replaceProductId: (id: string) => void;
  mutate: DataMutate<PIMProduct>;
  index: number;
  onChange: (index: number) => void;
  tabsKey?: string;
  tabsStyle?: CSSProperties;
  isPortfolio?: boolean;
  roleIsGuest?: boolean;
  roleIsBuyer?: boolean;
  onVisibilityDataFetched?: (visibilityData: IProductVisibilityData) => void;
  show_pricing_tab: boolean;
  isTabDisabled?: boolean;
  isShowTableWithBulkAction?: boolean;
  isShowGridAssetWithBulkAction?: boolean;
  documentType?: string;
  onRequestedTabChange?: (index: number) => void;
  isApprovalWorkflowEnabled?: boolean;
  isApprovalWorkflowLoading?: boolean;
  isDocumentAvailabilityLoading?: boolean;
  isListPricesLoading?: boolean;
  isRelatedEntitiesLoading?: boolean;
  onApprovalsCountChange?: (count: number) => void;
  hasAssetsOrExternalLinks: boolean;
  relatedEntities?: RelatedEntitiesResponse;
  onDocumentTypeTabChange?: (index: number) => void;
  requestedTabKey?: string;
}) => {
  const { roleIsSomeKindOfSeller } = useContext(Auth);
  const { storefront_metadata, tenant_id } = useStoreState();
  const { adminPath } = useRoutePath();
  const productTypePermissionSuffix = getProductTypePermissionSuffix(
    product.pim_product_type
  );

  const isActylisStorefront = useIsActylisStorefront();
  const { isXlScreen } = useMediaQueries();
  const pageLayout =
    storefront_metadata.theme_object.designTokens?.productDetail
      ?.product_detail_layout?.value || "single_column";

  const showChangeRequestTab =
    !isPortfolio && storefront_metadata.enable_change_requests;

  const tabDefinitions = useMemo(() => {
    const enable_list_prices = storefront_metadata.enable_list_prices;

    const effectiveEditRights =
      product.is_editable ||
      (product.is_locked && hasPermission("modify_products"));
    const shouldHideEmptyContent =
      isPortfolio ||
      (roleIsSomeKindOfSeller &&
        !(hasPermission("modify_products") && effectiveEditRights));

    const SEOGroup = product.product_schema.groups.find(
      (group) => group.name === "SEO"
    );

    const has_sku_values = product.product_skus?.some((sku) => !sku.is_sample);

    const can_view_list_prices =
      isPortfolio && roleIsGuest
        ? product.list_price_visibility === "available_everywhere"
        : roleIsBuyer
          ? product.list_price_visibility === "internal_and_customers" ||
          product.list_price_visibility === "available_everywhere"
          : true;

    const collectionHasValues = (collection: CollectionSchema) => {
      return (
        !!collection.value_rows &&
        collection.value_rows.length > 0 &&
        collection.value_rows.flatMap(({ values }) => values).length > 0
      );
    };

    const tabHasValues = (name: string) => {
      const lowerName = name.toLowerCase();

      if (lowerName === "sku") {
        const skusCollection = product.product_schema.collections.find(
          (collection) => collection.name.toLowerCase() === "skus"
        );
        return !!(
          skusCollection &&
          roleIsSomeKindOfSeller &&
          skusCollection.is_visible &&
          (!shouldHideEmptyContent || collectionHasValues(skusCollection))
        );
      }

      const tab = product.product_schema.layout.find(
        (tab) => tab.name === name
      );
      if (!tab?.items?.length) return false;

      return tab.items.some((item) => {
        if (item.type === "collection") {
          const collection = product.product_schema.collections.find(
            (c) => c.name.toLowerCase() === item.name.toLowerCase()
          );
          return !!(
            collection?.is_visible &&
            (!shouldHideEmptyContent ||
              (collectionHasValues(collection) &&
                (!collection.is_restricted || roleIsSomeKindOfSeller)))
          );
        } else {
          const group = product.product_schema.groups.find(
            (g) => g.name.toLowerCase() === item.name.toLowerCase()
          );
          const hasAttributes = !!group?.attributes?.length;
          const isNotSEO = group?.name.toLowerCase() !== "seo";
          const canShowGroup =
            (hasAttributes &&
              isNotSEO &&
              groupHasValues(group) &&
              ((!group.is_restricted && roleIsGuest) || !roleIsGuest)) ||
            !shouldHideEmptyContent;

          return canShowGroup;
        }
      });
    };

    const groupHasValues = (group: AttributeGroupSummary) => {
      let hasValues = false;
      const is_accessible = (accessibility?: SupportedAssetAccessibility) => {
        return (
          (roleIsGuest && accessibility === "public") ||
          accessibility !== "internal"
        );
      };
      group?.attributes?.forEach((attribute) => {
        if (
          attribute.values?.length > 0 &&
          ((!shouldHideEmptyContent && attribute.is_visible) ||
            (shouldHideEmptyContent &&
              roleIsGuest &&
              !attribute.is_restricted &&
              !attribute.is_not_applicable &&
              attribute.is_visible) ||
            (shouldHideEmptyContent &&
              !roleIsGuest &&
              attribute.is_visible &&
              !attribute.is_not_applicable) ||
            (shouldHideEmptyContent &&
              attribute.input_type === "image" &&
              is_accessible(attribute.values[0]?.visibility)))
        ) {
          hasValues = true;
        }
      });
      return hasValues;
    };

    const showSEOTab = (group: AttributeGroupSummary) => {
      return (
        !roleIsGuest &&
        !isPortfolio &&
        group?.attributes?.some((attribute) => attribute.is_visible)
      );
    };

    const NewTabsList: TabDefinition[] = [];

    product.product_schema.layout?.forEach((tab: TemplateTabData) => {
      if (tab.is_enabled) {
        switch (tab.tab_type) {
          case "documents":
            if (
              hasPermission("view_assets") ||
              roleIsBuyer ||
              roleIsGuest ||
              roleIsSomeKindOfSeller
            ) {
              const roleIsNonSeller = roleIsGuest || roleIsBuyer;
              const hasDocumentsContent =
                isDocumentAvailabilityLoading || hasAssetsOrExternalLinks;
              if (
                (roleIsNonSeller && hasDocumentsContent) ||
                (hasPermission("view_assets") &&
                  !hasDocumentsContent &&
                  !shouldHideEmptyContent) ||
                (hasPermission("view_assets") &&
                  hasDocumentsContent &&
                  shouldHideEmptyContent) ||
                (roleIsSomeKindOfSeller && !shouldHideEmptyContent) ||
                (hasDocumentsContent &&
                  shouldHideEmptyContent &&
                  roleIsSomeKindOfSeller)
              ) {
                NewTabsList.push({
                  key: tab.name,
                  label: tab.display_name,
                  analyticsName: tab.name,
                  renderPanel: (isTabActive) =>
                    withTabLoadingPanel(
                      isTabActive,
                      isDocumentAvailabilityLoading,
                      <ProductAssets
                        product={product}
                        fetchProductData={mutate}
                        replaceProductId={replaceProductId}
                        viewMode={isPortfolio}
                        isShowTableWithBulkAction={isShowTableWithBulkAction}
                        isShowGridAssetWithBulkAction={
                          isShowGridAssetWithBulkAction
                        }
                        documentType={documentType}
                      />
                    ),
                });
              }
            }
            break;

          case "seo":
            if (
              SEOGroup &&
              (!shouldHideEmptyContent || groupHasValues(SEOGroup)) &&
              showSEOTab(SEOGroup)
            ) {
              NewTabsList.push({
                key: tab.name,
                label: tab.display_name,
                analyticsName: tab.name,
                renderPanel: () => (
                  <>
                    <ProductGroupCard
                      product={product}
                      group={SEOGroup}
                      key={tab.name}
                      mutateProduct={mutate}
                      isPortfolio={false}
                      replaceProductId={replaceProductId}
                    />
                    <SchemaTagCard product={product} />
                  </>
                ),
              });
            }
            break;

          default:
            const tabItems = tab.items;
            const cardTab = { ...tab };
            if (
              tab.tab_type === "overview" &&
              isXlScreen &&
              pageLayout === "two_column"
            ) {
              let items = cardTab.items.filter(
                (item) => item.name !== "General"
              );
              cardTab.items = items;
            } else {
              cardTab.items = tabItems;
            }
            if (tabHasValues(tab.name)) {
              NewTabsList.push({
                key: tab.name,
                label: tab.display_name,
                analyticsName: tab.name,
                renderPanel: () => (
                  <ProductTabPanel
                    tab={cardTab}
                    product={product}
                    mutateProduct={mutate}
                    replaceProductId={replaceProductId}
                    isPortfolio={isPortfolio}
                  />
                ),
              });
            }
            break;
        }
      }
    });

    if (!shouldHideEmptyContent) {
      if (
        hasPermission("modify_products") &&
        hasPermission(`modify_${productTypePermissionSuffix}`) &&
        !isActylisStorefront
      ) {
        NewTabsList.push({
          key: "access",
          label: t("Access"),
          analyticsName: "Access",
          renderPanel: () => (
            <AccessHomePage product={product} mutateProduct={mutate} />
          ),
        });
      }
      NewTabsList.push({
        key: "connections",
        label: t("Connections"),
        analyticsName: "Connections",
        renderPanel: () => (
          <ProductConnections
            product={product}
            onVisibilityDataFetched={onVisibilityDataFetched}
          />
        ),
      });

      if (
        has_sku_values &&
        enable_list_prices &&
        product.pim_product_type !== "master"
      ) {
        NewTabsList.push({
          key: "pricing",
          label: t("Pricing"),
          analyticsName: "Pricing",
          renderPanel: () => (
            <ProductPricingTab
              product={product}
              mutate_product={mutate}
              replace_product_id={replaceProductId}
            />
          ),
        });
      }

      const showApprovalsTab =
        storefront_metadata.enable_approval_workflow &&
        hasPermission("modify_products") &&
        (isApprovalWorkflowLoading || isApprovalWorkflowEnabled);

      if (showApprovalsTab) {
        NewTabsList.push({
          key: "approvals",
          label: t("Approvals"),
          analyticsName: "Approvals",
          renderPanel: (isTabActive) =>
            withTabLoadingPanel(
              isTabActive,
              isApprovalWorkflowLoading,
              <Approvals onCountChange={onApprovalsCountChange} />
            ),
        });
      }

      const showRelatedEntitiesTab =
        storefront_metadata.object_reference_enabled &&
        (isRelatedEntitiesLoading ||
          getShouldShowRelatedEntitiesTab({
            objectReferenceEnabled:
              storefront_metadata.object_reference_enabled,
            relatedEntities,
          }));

      if (showRelatedEntitiesTab) {
        NewTabsList.push({
          key: "related_entities",
          label: t("Related Entities"),
          analyticsName: "Related Entities",
          renderPanel: (isTabActive) =>
            withTabLoadingPanel(
              isTabActive,
              isRelatedEntitiesLoading,
              relatedEntities ? (
                <RelatedEntitiesTabContent
                  tenantIdOrSlug={tenant_id}
                  targetObjectType="product"
                  targetObjectId={product.id}
                  relatedEntities={relatedEntities}
                  getSourceHref={(source) =>
                    source.object_type === "product"
                      ? `${adminPath}/pim/products/${source.id} `
                      : `${adminPath} /mdm/${source.object_type}s/${source.id} `
                  }
                />
              ) : null
            ),
        });
      }
    }

    if (showChangeRequestTab) {
      NewTabsList.push({
        key: "change_request",
        label: t("Change Requests"),
        analyticsName: "Change Requests",
        renderPanel: (isTabActive) => (
          <div>
            <ChangeRequestsListPage
              entityId={product.id}
              isTabActive={isTabActive}
              canEdit={
                hasPermission(`modify_${productTypePermissionSuffix}`) &&
                hasPermission("modify_products")
              }
            />
          </div>
        ),
      });
    }

    if (!shouldHideEmptyContent) {
      NewTabsList.push({
        key: "activity",
        label: t("Activity"),
        analyticsName: "Activity",
        renderPanel: (isTabActive) => (
          <div>
            <ActivityHistory
              targetId={
                product.display_status === "staged" ||
                  product.display_status === "unpublished_staged"
                  ? product.published_product_id
                  : product.id
              }
              targetType="product"
              isTabActive={isTabActive}
            />
          </div>
        ),
      });
    }

    if (enable_list_prices && (isListPricesLoading || show_pricing_tab)) {
      NewTabsList.push({
        key: "pricing",
        label: t("Pricing"),
        analyticsName: "Pricing",
        renderPanel: (isTabActive) =>
          withTabLoadingPanel(
            isTabActive,
            isListPricesLoading,
            <ProductPricingPortfolioTab
              product={product}
              can_view_list_prices={can_view_list_prices && enable_list_prices}
            />
          ),
      });
    }

    return NewTabsList;
  }, [
    hasPermission,
    show_pricing_tab,
    isPortfolio,
    isShowGridAssetWithBulkAction,
    isShowTableWithBulkAction,
    mutate,
    onVisibilityDataFetched,
    product,
    replaceProductId,
    roleIsBuyer,
    roleIsGuest,
    roleIsSomeKindOfSeller,
    storefront_metadata.enable_list_prices,
    storefront_metadata.enable_approval_workflow,
    storefront_metadata.object_reference_enabled,
    t,
    documentType,
    productTypePermissionSuffix,
    isListPricesLoading,
    isXlScreen,
    pageLayout,
    isDocumentAvailabilityLoading,
    hasAssetsOrExternalLinks,
    isActylisStorefront,
    isApprovalWorkflowLoading,
    isApprovalWorkflowEnabled,
    isRelatedEntitiesLoading,
    relatedEntities,
    showChangeRequestTab,
    onApprovalsCountChange,
    tenant_id,
    adminPath,
  ]);

  const selectTabIndex = onRequestedTabChange ?? onChange;

  const activeTabKeyRef = useRef<string | null>(null);
  const documentTypeNavigatedRef = useRef<string | null>(null);

  useEffect(() => {
    if (index >= 0 && index < tabDefinitions.length) {
      activeTabKeyRef.current = tabDefinitions[index]?.key ?? null;
    }
  }, [index, tabDefinitions]);

  useEffect(() => {
    if (!tabDefinitions.length) {
      return;
    }

    // URL-requested tab wins over the numeric index and over "keep the tab
    // that happened to be active while the list was still loading".
    if (requestedTabKey) {
      const requestedIndex = tabDefinitions.findIndex(
        (tab) => tab.key === requestedTabKey
      );
      if (requestedIndex >= 0 && requestedIndex !== index) {
        selectTabIndex(requestedIndex);
      }
      return;
    }

    const activeKey = activeTabKeyRef.current;
    if (activeKey) {
      const nextIndex = tabDefinitions.findIndex(
        (tab) => tab.key === activeKey
      );
      if (nextIndex >= 0) {
        if (nextIndex !== index) {
          onChange(nextIndex);
        }
        return;
      }
      const fallbackIndex = Math.min(
        Math.max(index, 0),
        tabDefinitions.length - 1
      );
      activeTabKeyRef.current = tabDefinitions[fallbackIndex]?.key ?? null;
      if (fallbackIndex !== index) {
        onChange(fallbackIndex);
      }
      return;
    }

    if (index >= tabDefinitions.length) {
      onChange(Math.max(0, tabDefinitions.length - 1));
    }
  }, [tabDefinitions, index, onChange, requestedTabKey, selectTabIndex]);

  useEffect(() => {
    if (
      !documentType ||
      !tabDefinitions.length ||
      isDocumentAvailabilityLoading
    ) {
      return;
    }

    const documentsLayoutTab = product.product_schema.layout?.find(
      (tab) => tab.tab_type === "documents" && tab.is_enabled
    );
    if (!documentsLayoutTab) {
      return;
    }

    const documentsTabIndex = tabDefinitions.findIndex(
      (tab) => tab.key === documentsLayoutTab.name
    );
    if (documentsTabIndex < 0 || documentsTabIndex === index) {
      return;
    }

    const navigationKey = `${product.id}:${documentType}`;
    if (documentTypeNavigatedRef.current === navigationKey) {
      return;
    }
    documentTypeNavigatedRef.current = navigationKey;

    if (onDocumentTypeTabChange) {
      onDocumentTypeTabChange(documentsTabIndex);
    } else {
      onChange(documentsTabIndex);
    }
  }, [
    documentType,
    tabDefinitions,
    isDocumentAvailabilityLoading,
    product.id,
    product.product_schema.layout,
    index,
    onChange,
    onDocumentTypeTabChange,
  ]);

  useEffect(() => {
    documentTypeNavigatedRef.current = null;
  }, [product.id, documentType]);

  return (
    <Tabs
      key={`${tabsKey ?? product.id}:${tabDefinitions
        .map((t) => t.key)
        .join("|")}`}
      onChange={onChange}
      index={index}
      style={tabsStyle}
    >
      <ProductProvider product={product}>
        <TabList disabled={isTabDisabled}>
          {tabDefinitions.map((tab) => (
            <ProductTab
              key={tab.key}
              data-analytics-name={tab.analyticsName}
              data-object-id={product.product_number ?? product.id}
            >
              {tab.label}
            </ProductTab>
          ))}
        </TabList>
        <TabPanels>
          {tabDefinitions.map((tab, tabIndex) => {
            const isTabActive = index === tabIndex;
            return (
              <LazyTabPanel
                key={tab.key}
                refreshKey={tab.key}
                isSelected={isTabActive}
              >
                {tab.renderPanel(isTabActive)}
              </LazyTabPanel>
            );
          })}
        </TabPanels>
      </ProductProvider>
    </Tabs>
  );
};
